import React, { useMemo } from 'react';
import { CADENCE_HEATMAP_RAMP, sortCompetitorCadenceByWeeks } from '../../utils/chartUtils';

const hexToHsl = (hex) => {
  if (!hex) return { h: 0, s: 0, l: 100 };
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

const CompetitorCadenceHeatmap = ({
  data,
  weeks = ['Wk 14', 'Wk 15', 'Wk 16', 'Wk 17', 'Wk 18'],
  setTooltip,
  preserveColumnOrder = false,
  onCellClick,
  selectedCell,
  rowLabelHint = 'Competitor summary',
  drillHint,
}) => {
  const { weeks: sortedWeeks, rows: sortedData } = useMemo(
    () => (
      preserveColumnOrder
        ? { weeks: weeks || [], rows: data || [] }
        : sortCompetitorCadenceByWeeks(weeks, data || [])
    ),
    [weeks, data, preserveColumnOrder],
  );

  if (!sortedData.length) return null;

  const columnCount = sortedWeeks.length;
  const rowCount = sortedData.length;
  const totalSlots = Math.max(1, columnCount * rowCount);
  const slotDenom = Math.max(1, totalSlots - 1);
  const rowDenom = Math.max(1, rowCount - 1);

  const maxV = Math.max(1, ...sortedData.flatMap((row) => row.vals || []));

  const handleCellActivate = (rowIdx, colIdx, row, value) => {
    if (!onCellClick || value <= 0) return;
    onCellClick({ rowIndex: rowIdx, columnIndex: colIdx, row, value });
  };

  return (
    <div className="cadence-heatmap-container">
      <div className="cadence-heatmap-header">
        <div className="cadence-heatmap-header-spacer" aria-hidden="true" />
        <div className="cadence-heatmap-header-weeks">
          {sortedWeeks.map((wk, idx) => (
            <div key={idx} className="cadence-heatmap-header-col">
              {wk}
            </div>
          ))}
        </div>
      </div>

      {sortedData.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className="cadence-heatmap-row"
          style={{ '--heatmap-row-delay': rowDenom > 0 ? rowIdx / rowDenom : 0 }}
        >
          <div
            className="cadence-heatmap-company-lbl"
            onMouseEnter={(e) => {
              if (!setTooltip) return;
              const totalRow = row.vals ? row.vals.reduce((a, b) => a + b, 0) : 0;
              setTooltip({
                x: e.clientX,
                y: e.clientY,
                content: (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: '2px' }}>{row.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text2)' }}>
                      {row.ta ? 'Our company' : rowLabelHint}
                    </div>
                    <div style={{ marginTop: '4px', fontWeight: 600 }}>{totalRow} Total Filings</div>
                  </>
                ),
              });
            }}
            onMouseMove={(e) => {
              if (!setTooltip) return;
              setTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null));
            }}
            onMouseLeave={() => setTooltip && setTooltip(null)}
          >
            <span className="cadence-heatmap-company-name" title={row.name}>
              <span className="cadence-heatmap-company-dot-slot" aria-hidden="true">
                {row.ta ? <span className="cadence-heatmap-company-dot" /> : null}
              </span>
              <span className="cadence-heatmap-company-name__text">{row.name}</span>
            </span>
          </div>

          <div className="cadence-heatmap-cells">
            {row.vals.map((v, i) => {
              if (v === 0) {
                return (
                  <div key={i} className="cadence-heatmap-cell-empty">
                    —
                  </div>
                );
              }

              const ratio = v / maxV;
              const rampIdx = Math.min(
                CADENCE_HEATMAP_RAMP.length - 1,
                Math.floor(ratio * CADENCE_HEATMAP_RAMP.length),
              );
              const cellBg = CADENCE_HEATMAP_RAMP[rampIdx];
              const hsl = hexToHsl(cellBg);
              const textColor = hsl.l < 65 ? '#FFFFFF' : 'var(--c-black)';
              const slotIndex = rowIdx * columnCount + i;
              const isClickable = Boolean(onCellClick);
              const isSelected = selectedCell?.rowIndex === rowIdx && selectedCell?.columnIndex === i;

              return (
                <div
                  key={i}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  className={`cadence-heatmap-cell-active${isSelected ? ' is-selected' : ''}`}
                  aria-pressed={isClickable ? isSelected : undefined}
                  style={{
                    background: cellBg,
                    color: textColor,
                    fontWeight: 600,
                    '--heatmap-cell-delay': slotIndex / slotDenom,
                    cursor: isClickable ? 'pointer' : undefined,
                  }}
                  onClick={() => handleCellActivate(rowIdx, i, row, v)}
                  onKeyDown={(event) => {
                    if (!isClickable) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleCellActivate(rowIdx, i, row, v);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!setTooltip) return;
                    setTooltip({
                      x: e.clientX,
                      y: e.clientY,
                      content: (
                        <>
                          <div style={{ fontWeight: 700, marginBottom: '2px' }}>{row.name}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{sortedWeeks[i]}</div>
                          <div style={{ marginTop: '4px', fontWeight: 600 }}>
                            {v}
                            {' '}
                            filing
                            {v === 1 ? '' : 's'}
                          </div>
                          {drillHint ? (
                            <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '4px' }}>
                              {drillHint}
                            </div>
                          ) : null}
                        </>
                      ),
                    });
                  }}
                  onMouseMove={(e) => {
                    if (!setTooltip) return;
                    setTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null));
                  }}
                  onMouseLeave={() => setTooltip && setTooltip(null)}
                >
                  {v}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompetitorCadenceHeatmap;
